import type { RouteLocationNormalized } from 'vue-router'

export interface ResolvedAnalyticsContext {
  locationId: string
  contactId?: string
  userId?: string
  gokollabContactId?: string
  tenantContextStatus?: 'resolved' | 'fallback_timeout' | 'fallback_error'
}

export interface AnalyticsResolutionOptions {
  signal: AbortSignal
}

export type AnalyticsContextResolver = (
  route: RouteLocationNormalized,
  options: AnalyticsResolutionOptions
) => ResolvedAnalyticsContext | Promise<ResolvedAnalyticsContext>

export type AnalyticsContextFailurePolicy = 'fallback' | 'fail-closed'

/**
 * Supplies the global (non-tenant) identity for the default resolver. Injected by the common
 * analytics plugin so this module does not import the location/auth stores directly — that would
 * form an analytics-layer ↔ auth-layer import cycle (auth-layer already imports analytics types).
 */
export interface DefaultIdentitySource {
  getLocationId: () => string
  getContactId: () => string | null | undefined
  getUserId?: () => string | null | undefined
}

let resolver: AnalyticsContextResolver | null = null
let resolverFailurePolicy: AnalyticsContextFailurePolicy = 'fallback'
let defaultIdentity: DefaultIdentitySource | null = null

/** Apps with tenant-scoped analytics (gokollab) register a resolver at plugin setup. */
export function registerAnalyticsContextResolver(
  fn: AnalyticsContextResolver,
  options: { failurePolicy?: AnalyticsContextFailurePolicy } = {}
): void {
  resolver = fn
  resolverFailurePolicy = options.failurePolicy ?? 'fallback'
}

export function getAnalyticsContextFailurePolicy(): AnalyticsContextFailurePolicy {
  return resolver ? resolverFailurePolicy : 'fallback'
}

/** The common plugin registers the global identity source (location + auth) at setup. */
export function registerDefaultIdentitySource(source: DefaultIdentitySource): void {
  defaultIdentity = source
}

/** Default: resolve immediately from the injected global identity (clientportal behavior). */
function defaultResolver(): ResolvedAnalyticsContext {
  const locationId = defaultIdentity?.getLocationId() || ''
  const contactId = defaultIdentity?.getContactId() || undefined
  const userId = defaultIdentity?.getUserId?.() || undefined
  return {
    locationId,
    ...(locationId && contactId ? { contactId } : {}),
    ...(userId ? { userId } : {}),
  }
}

/** Read the registered resolver lazily per navigation so late registration still wins. */
export function resolveAnalyticsContext(
  route: RouteLocationNormalized,
  options: AnalyticsResolutionOptions
): ReturnType<AnalyticsContextResolver> {
  return resolver ? resolver(route, options) : defaultResolver()
}

export function resolveDefaultAnalyticsContext(): ResolvedAnalyticsContext {
  return defaultResolver()
}

/** Test hook. */
export function resetAnalyticsContextResolver(): void {
  resolver = null
  resolverFailurePolicy = 'fallback'
}
